import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, subDays, parseISO } from "date-fns";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  RefreshCw,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  ArrowUp,
  ArrowDown,
  Minus,
  Info,
  Database,
  ExternalLink,
  AlertTriangle,
  BarChart3,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlatformMultiSelect, AccountMultiSelect } from "./CommonComponents";
import { API, formatNumber, getPlatformBadgeClass, PLATFORM_METRIC_AVAILABILITY } from "./constants";
import GrowthTrendModal from "./GrowthTrendModal";

// Translations object
const TRANSLATIONS = {
  en: {
    title: "Content Growth",
    subtitle: "Track performance changes over time",
    selectPeriod: "Select Period",
    startDate: "Start Date",
    endDate: "End Date",
    analyze: "Analyze Growth",
    analyzing: "Analyzing...",
    noData: "No growth data available yet",
    noDataDescription: "Daily metrics are collected at CST 00:00. Data collection starts from today.",
    goodPerformers: "Good Performers",
    poorPerformers: "Poor Performers",
    viewsDelta: "Views",
    likesDelta: "Likes",
    commentsDelta: "Comments",
    sharesDelta: "Shares",
    savesDelta: "Saves",
    periodStart: "Period Start",
    periodEnd: "Period End",
    delta: "Change",
    allContent: "All Content Growth",
    sortBy: "Sort by",
    totalGrowth: "Total Growth",
    postsAnalyzed: "Posts Analyzed",
    manualSnapshot: "Save Snapshot Now",
    snapshotSaved: "Snapshot saved",
    scheduleInfo: "Data collected daily at 00:00 CST",
    summary: "Summary",
    content: "Content",
    platform: "Platform",
    growth: "Growth",
    noGoodPerformers: "No good performers found",
    noPoorPerformers: "No poor performers found",
    goodPerformersDesc: "Top growing content during selected period",
    poorPerformersDesc: "Content with declining or stagnant growth",
    allContentDesc: "All content growth data from",
    to: "to",
    minDateWarning: "Data collection started on",
    viewTrend: "View Trend",
    sortOptions: {
      likes_delta: "Likes Growth",
      views_delta: "Views Growth",
      comments_delta: "Comments Growth",
      shares_delta: "Shares Growth",
      saves_delta: "Saves Growth",
    },
  },
  ko: {
    title: "콘텐츠 성장 분석",
    subtitle: "시간에 따른 콘텐츠 퍼포먼스 변화 추적",
    selectPeriod: "기간 선택",
    startDate: "시작일",
    endDate: "종료일",
    analyze: "성장 분석",
    analyzing: "분석 중...",
    noData: "아직 성장 데이터가 없습니다",
    noDataDescription: "일별 메트릭은 매일 중국 시간 00:00에 수집됩니다. 오늘부터 데이터 수집이 시작됩니다.",
    goodPerformers: "성장 우수 콘텐츠",
    poorPerformers: "성장 부진 콘텐츠",
    viewsDelta: "조회수",
    likesDelta: "좋아요",
    commentsDelta: "댓글",
    sharesDelta: "공유",
    savesDelta: "저장",
    periodStart: "기간 시작",
    periodEnd: "기간 종료",
    delta: "변화량",
    allContent: "전체 콘텐츠 성장",
    sortBy: "정렬 기준",
    totalGrowth: "총 성장",
    postsAnalyzed: "분석된 게시물",
    manualSnapshot: "지금 스냅샷 저장",
    viewTrend: "추이 보기",
    snapshotSaved: "스냅샷 저장됨",
    scheduleInfo: "매일 중국 시간 00:00에 데이터 수집",
    summary: "요약",
    content: "콘텐츠",
    platform: "플랫폼",
    growth: "성장",
    noGoodPerformers: "성장 우수 콘텐츠가 없습니다",
    noPoorPerformers: "성장 부진 콘텐츠가 없습니다",
    goodPerformersDesc: "선택 기간 동안 가장 많이 성장한 콘텐츠",
    poorPerformersDesc: "선택 기간 동안 성장이 부진한 콘텐츠",
    allContentDesc: "전체 콘텐츠 성장 데이터",
    to: "~",
    minDateWarning: "데이터 수집 시작일:",
    viewTrend: "추이 보기",
    sortOptions: {
      likes_delta: "좋아요 증가순",
      views_delta: "조회수 증가순",
      comments_delta: "댓글 증가순",
      shares_delta: "공유 증가순",
      saves_delta: "저장 증가순",
    },
  },
  zh: {
    title: "内容增长分析",
    subtitle: "追踪内容性能随时间的变化",
    selectPeriod: "选择时间段",
    startDate: "开始日期",
    endDate: "结束日期",
    analyze: "分析增长",
    analyzing: "分析中...",
    noData: "暂无增长数据",
    noDataDescription: "每日指标在中国时间00:00收集。从今天开始收集数据。",
    goodPerformers: "增长优秀内容",
    poorPerformers: "增长较弱内容",
    viewsDelta: "浏览量",
    likesDelta: "点赞",
    commentsDelta: "评论",
    sharesDelta: "分享",
    savesDelta: "收藏",
    periodStart: "期间开始",
    periodEnd: "期间结束",
    delta: "变化量",
    allContent: "全部内容增长",
    sortBy: "排序",
    totalGrowth: "总增长",
    postsAnalyzed: "已分析帖子",
    manualSnapshot: "立即保存快照",
    snapshotSaved: "快照已保存",
    scheduleInfo: "数据每天中国时间00:00收集",
    summary: "摘要",
    content: "内容",
    platform: "平台",
    growth: "增长",
    noGoodPerformers: "没有增长优秀的内容",
    noPoorPerformers: "没有增长较弱的内容",
    goodPerformersDesc: "选定期间内增长最快的内容",
    poorPerformersDesc: "增长停滞或下降的内容",
    allContentDesc: "所有内容增长数据",
    to: "至",
    minDateWarning: "数据收集开始日期:",
    viewTrend: "查看趋势",
    sortOptions: {
      likes_delta: "点赞增长",
      views_delta: "浏览量增长",
      comments_delta: "评论增长",
      shares_delta: "分享增长",
      saves_delta: "收藏增长",
    },
  },
};

// Calculate percentage change
const calcPercentChange = (start, delta) => {
  if (start === 0) return delta > 0 ? null : 0; // null means "NEW" (no base to compare)
  return Math.round((delta / start) * 100 * 10) / 10;
};

// Growth indicator component with percentage
const GrowthIndicator = ({ value, percentChange, compact = false, isNewContent = false }) => {
  if (value === 0 && (percentChange === 0 || percentChange === null)) {
    return (
      <span className="flex items-center text-muted-foreground text-sm">
        <Minus className="h-3 w-3 mr-1" />
        <span>0</span>
      </span>
    );
  }

  const isPositive = value > 0;
  const Icon = isPositive ? ArrowUp : ArrowDown;
  const colorClass = isPositive ? "text-green-500" : "text-red-500";

  return (
    <span className={`flex items-center gap-1 ${colorClass} text-sm`}>
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="font-medium">{isPositive ? "+" : ""}{formatNumber(value)}</span>
      {percentChange !== null && percentChange !== undefined && !compact && (
        <span className="text-xs opacity-70">({isPositive ? "+" : ""}{percentChange}%)</span>
      )}
      {isNewContent && !compact && (
        <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1 bg-blue-500/10 text-blue-500 border-blue-500/30">NEW</Badge>
      )}
    </span>
  );
};

// Platform badge component
const PlatformBadge = ({ platform }) => (
  <Badge variant="secondary" className={`${getPlatformBadgeClass(platform)} text-xs`}>
    {platform}
  </Badge>
);

// Metric growth display component
const MetricGrowthDisplay = ({ icon: Icon, iconColor, delta, startValue, label, unavailable }) => {
  const percentChange = calcPercentChange(startValue, delta);
  
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`h-3.5 w-3.5 ${iconColor} flex-shrink-0`} />
      {unavailable ? (
        <span className="text-xs text-muted-foreground">N/A</span>
      ) : (
        <GrowthIndicator value={delta} percentChange={percentChange} compact={false} />
      )}
    </div>
  );
};

// Performer card component - now shows individual metrics instead of growth rate
const PerformerCard = ({ post, isGood, t, onViewTrend }) => (
  <div className="p-3 bg-card border rounded-lg hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{post.title || "Untitled"}</p>
        <div className="flex items-center gap-2 mt-1">
          <PlatformBadge platform={post.platform} />
          <span className="text-xs text-muted-foreground">
            {post.published_at?.slice(0, 10)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onViewTrend(post)}
          className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-primary"
          title={t.viewTrend}
        >
          <BarChart3 className="h-4 w-4" />
        </button>
        {post.post_url && (
          <a
            href={post.post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-primary"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
    
    {/* Individual metric deltas */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 pt-3 border-t border-border/50">
      <MetricGrowthDisplay
        icon={Eye}
        iconColor="text-blue-500"
        delta={post.delta?.views || 0}
        startValue={post.metrics_start?.views || 0}
        label={t.viewsDelta}
      />
      <MetricGrowthDisplay
        icon={Heart}
        iconColor="text-red-500"
        delta={post.delta?.likes || 0}
        startValue={post.metrics_start?.likes || 0}
        label={t.likesDelta}
      />
      <MetricGrowthDisplay
        icon={MessageCircle}
        iconColor="text-amber-500"
        delta={post.delta?.comments || 0}
        startValue={post.metrics_start?.comments || 0}
        label={t.commentsDelta}
      />
      <MetricGrowthDisplay
        icon={Share2}
        iconColor="text-purple-500"
        delta={PLATFORM_METRIC_AVAILABILITY[post.platform]?.shares === false ? null : (post.delta?.shares || 0)}
        startValue={post.metrics_start?.shares || 0}
        label={t.sharesDelta}
        unavailable={PLATFORM_METRIC_AVAILABILITY[post.platform]?.shares === false}
      />
    </div>
  </div>
);

const ContentGrowthPage = ({ lang, accounts }) => {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  // State
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(() => new Date());
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [sortBy, setSortBy] = useState("likes_delta");
  const [loading, setLoading] = useState(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [growthData, setGrowthData] = useState(null);
  const [minSnapshotDate, setMinSnapshotDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  
  // Modal state for trend chart
  const [trendModalOpen, setTrendModalOpen] = useState(false);
  const [selectedPostForTrend, setSelectedPostForTrend] = useState(null);

  // Handler to open trend modal
  const handleViewTrend = (post) => {
    setSelectedPostForTrend(post);
    setTrendModalOpen(true);
  };

  // Fetch available snapshot dates on mount
  useEffect(() => {
    const fetchAvailableDates = async () => {
      try {
        const response = await axios.get(`${API}/analytics/growth-summary`);
        const dates = response.data.available_dates || [];
        setAvailableDates(dates);
        
        if (dates.length > 0) {
          // Sort dates ascending and get the earliest
          const sortedDates = [...dates].sort();
          const earliestDate = sortedDates[0];
          const minDate = parseISO(earliestDate);
          setMinSnapshotDate(minDate);
          
          // Set default start date to earliest available date
          setStartDate(minDate);
        } else {
          // No data yet - set to today
          setStartDate(new Date());
        }
      } catch (error) {
        console.error("Failed to fetch available dates:", error);
        // Fallback to today
        setStartDate(new Date());
      }
    };
    
    fetchAvailableDates();
  }, []);

  // Fetch growth data
  const fetchGrowthData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      const params = {
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        sort_by: sortBy,
        order: "desc",
      };

      if (selectedPlatforms.length > 0) {
        params.platform = selectedPlatforms.join(",");
      }
      if (selectedAccounts.length > 0) {
        params.account_id = selectedAccounts.join(",");
      }

      const response = await axios.get(`${API}/analytics/content-growth`, { params });
      setGrowthData(response.data);
    } catch (error) {
      console.error("Failed to fetch growth data:", error);
      toast.error("Failed to load growth data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedPlatforms, selectedAccounts, sortBy]);

  // Manual snapshot trigger
  const triggerManualSnapshot = async () => {
    setSavingSnapshot(true);
    try {
      await axios.post(`${API}/scheduler/metrics-snapshot-trigger`);
      toast.success(t.snapshotSaved);
      // Refresh available dates
      const response = await axios.get(`${API}/analytics/growth-summary`);
      setAvailableDates(response.data.available_dates || []);
    } catch (error) {
      console.error("Failed to save snapshot:", error);
      toast.error("Failed to save snapshot");
    } finally {
      setSavingSnapshot(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchGrowthData();
    }
  }, [fetchGrowthData, startDate, endDate]);

  // Disable dates before first snapshot
  const disabledDates = (date) => {
    if (!minSnapshotDate) return false;
    return date < minSnapshotDate;
  };

  return (
    <div className="space-y-6" data-testid="content-growth-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            {t.title}
          </h2>
          <p className="text-muted-foreground text-sm">{t.subtitle}</p>
        </div>

        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={triggerManualSnapshot}
                  disabled={savingSnapshot}
                  data-testid="manual-snapshot-btn"
                >
                  {savingSnapshot ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Database className="h-4 w-4" />
                  )}
                  <span className="ml-2 hidden sm:inline">{t.manualSnapshot}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t.scheduleInfo}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Min Date Info */}
      {minSnapshotDate && (
        <Alert variant="default" className="bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            {t.minDateWarning} <strong>{format(minSnapshotDate, "yyyy-MM-dd")}</strong>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Start Date */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t.startDate}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="growth-start-date">
                    <Calendar className="h-4 w-4 mr-2" />
                    {startDate ? format(startDate, "yyyy-MM-dd") : t.startDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    disabled={disabledDates}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t.endDate}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="growth-end-date">
                    <Calendar className="h-4 w-4 mr-2" />
                    {endDate ? format(endDate, "yyyy-MM-dd") : t.endDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    disabled={disabledDates}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Platform Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t.platform}</label>
              <PlatformMultiSelect
                selected={selectedPlatforms}
                onChange={setSelectedPlatforms}
                lang={lang}
              />
            </div>

            {/* Account Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Account</label>
              <AccountMultiSelect
                accounts={accounts}
                selected={selectedAccounts}
                onChange={setSelectedAccounts}
                selectedPlatforms={selectedPlatforms}
                lang={lang}
              />
            </div>

            {/* Sort By */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t.sortBy}</label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="growth-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="likes_delta">{t.sortOptions.likes_delta}</SelectItem>
                  <SelectItem value="views_delta">{t.sortOptions.views_delta}</SelectItem>
                  <SelectItem value="comments_delta">{t.sortOptions.comments_delta}</SelectItem>
                  <SelectItem value="shares_delta">{t.sortOptions.shares_delta}</SelectItem>
                  <SelectItem value="saves_delta">{t.sortOptions.saves_delta}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Analyze Button */}
          <div className="mt-4 flex justify-end">
            <Button onClick={fetchGrowthData} disabled={loading || !startDate || !endDate} data-testid="analyze-growth-btn">
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t.analyzing}
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {t.analyze}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* No Data Alert */}
      {growthData && !growthData.data_available && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>{t.noData}</AlertTitle>
          <AlertDescription>
            {t.noDataDescription}
            {growthData.message && <p className="mt-2 text-sm">{growthData.message}</p>}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      {growthData?.data_available && growthData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{growthData.summary.total_posts_analyzed}</div>
              <div className="text-sm text-muted-foreground">{t.postsAnalyzed}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold flex items-center">
                <Eye className="h-5 w-5 mr-2 text-blue-500" />
                <GrowthIndicator value={growthData.summary.total_views_delta} compact />
              </div>
              <div className="text-sm text-muted-foreground">{t.viewsDelta}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold flex items-center">
                <Heart className="h-5 w-5 mr-2 text-red-500" />
                <GrowthIndicator value={growthData.summary.total_likes_delta} compact />
              </div>
              <div className="text-sm text-muted-foreground">{t.likesDelta}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold flex items-center">
                <MessageCircle className="h-5 w-5 mr-2 text-amber-500" />
                <GrowthIndicator value={growthData.summary.total_comments_delta} compact />
              </div>
              <div className="text-sm text-muted-foreground">{t.commentsDelta}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold flex items-center">
                <Share2 className="h-5 w-5 mr-2 text-purple-500" />
                {selectedPlatforms.length > 0 && selectedPlatforms.every(p => PLATFORM_METRIC_AVAILABILITY[p]?.shares === false) ? (
                  <span className="text-muted-foreground text-base">N/A</span>
                ) : (
                  <GrowthIndicator value={growthData.summary.total_shares_delta} compact />
                )}
              </div>
              <div className="text-sm text-muted-foreground">{t.sharesDelta}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performers Grid */}
      {growthData?.data_available && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Good Performers */}
          <Card className="border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-500">
                <TrendingUp className="h-5 w-5" />
                {t.goodPerformers}
              </CardTitle>
              <CardDescription>
                {t.goodPerformersDesc}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {growthData.good_performers?.length > 0 ? (
                  growthData.good_performers.map((post, idx) => (
                    <PerformerCard key={post.post_id || idx} post={post} isGood={true} t={t} onViewTrend={handleViewTrend} />
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    {t.noGoodPerformers}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Poor Performers */}
          <Card className="border-red-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-500">
                <TrendingDown className="h-5 w-5" />
                {t.poorPerformers}
              </CardTitle>
              <CardDescription>
                {t.poorPerformersDesc}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {growthData.poor_performers?.length > 0 ? (
                  growthData.poor_performers.map((post, idx) => (
                    <PerformerCard key={post.post_id || idx} post={post} isGood={false} t={t} onViewTrend={handleViewTrend} />
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">
                    {t.noPoorPerformers}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* All Content Table */}
      {growthData?.data_available && growthData?.posts?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t.allContent}</CardTitle>
            <CardDescription>
              {`${t.allContentDesc} ${growthData.period?.start} ${t.to} ${growthData.period?.end}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[250px]">{t.content}</TableHead>
                    <TableHead>{t.platform}</TableHead>
                    <TableHead className="text-center w-[60px]">{t.viewTrend}</TableHead>
                    <TableHead className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Eye className="h-3.5 w-3.5 text-blue-500" />
                        {t.viewsDelta}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Heart className="h-3.5 w-3.5 text-red-500" />
                        {t.likesDelta}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <MessageCircle className="h-3.5 w-3.5 text-amber-500" />
                        {t.commentsDelta}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Share2 className="h-3.5 w-3.5 text-purple-500" />
                        {t.sharesDelta}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {growthData.posts.map((post, idx) => (
                    <TableRow key={post.post_id || idx}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="min-w-0">
                            <p className="font-medium truncate max-w-[200px]">{post.title || "Untitled"}</p>
                            <p className="text-xs text-muted-foreground">{post.published_at?.slice(0, 10)}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <PlatformBadge platform={post.platform} />
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleViewTrend(post)}
                          className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-primary"
                          title={t.viewTrend}
                        >
                          <BarChart3 className="h-4 w-4" />
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <GrowthIndicator
                          value={post.delta?.views || 0}
                          percentChange={calcPercentChange(post.metrics_start?.views || 0, post.delta?.views || 0)}
                          isNewContent={post.is_new_content}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <GrowthIndicator
                          value={post.delta?.likes || 0}
                          percentChange={calcPercentChange(post.metrics_start?.likes || 0, post.delta?.likes || 0)}
                          isNewContent={post.is_new_content}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <GrowthIndicator
                          value={post.delta?.comments || 0}
                          percentChange={calcPercentChange(post.metrics_start?.comments || 0, post.delta?.comments || 0)}
                          isNewContent={post.is_new_content}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {PLATFORM_METRIC_AVAILABILITY[post.platform]?.shares === false ? (
                          <span className="text-muted-foreground text-xs">N/A</span>
                        ) : (
                          <GrowthIndicator
                            value={post.delta?.shares || 0}
                            percentChange={calcPercentChange(post.metrics_start?.shares || 0, post.delta?.shares || 0)}
                            isNewContent={post.is_new_content}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Schedule Info */}
      <Alert variant="default" className="bg-blue-500/10 border-blue-500/30">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-700 dark:text-blue-300">
          {t.scheduleInfo}
        </AlertDescription>
      </Alert>

      {/* Growth Trend Modal */}
      <GrowthTrendModal
        isOpen={trendModalOpen}
        onClose={() => setTrendModalOpen(false)}
        initialPost={selectedPostForTrend}
        availablePosts={growthData?.posts || []}
        lang={lang}
        minDate={minSnapshotDate}
      />
    </div>
  );
};

export default ContentGrowthPage;
